import { tacheCreation } from '@admin-pmvb/core'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { Link, useNavigate } from '@tanstack/react-router'
import { useEffect, useId, useState } from 'react'
import { api, corps, ecrit, ErreurApi, lire, requeteMoi, requetes } from '../api'
import { confierPhotos } from '../pages/Photo'
import { useEtatFile } from '../pwa/etat-file'
import { dicteePossible, DUREE_MAX_MS, useDictee } from './Dictee'
import { Icone } from './Icone'

// Les deux gestes du téléphone, en tête de l'accueil : dicter une tâche, photographier un
// document. Un toucher suffit : le micro s'ouvre, ou l'appareil photo.
//
// Tâche dictée : l'audio est transcrit (Deepgram), Claude propose titre, entité, date et
// urgence ; le texte dicté reste les précisions. Tout se relit et se corrige avant « Créer la
// tâche » : rien n'est créé avant ce clic.

type Saisie = {
  titre: string
  texte: string
  entiteId: string
  dateDue: string
  urgente: boolean
}

const SAISIE_VIDE: Saisie = { titre: '', texte: '', entiteId: '', dateDue: '', urgente: false }

export function ActionsRapides() {
  const navigate = useNavigate()
  const { elements } = useEtatFile()
  const enAttente = new Set(elements.map((e) => e.envoiId)).size
  const moi = useQuery(requeteMoi).data
  const avecMicro = Boolean(moi?.dictee) && dicteePossible()
  const [ouvert, setOuvert] = useState(false)
  const [creee, setCreee] = useState<{ id: string; titre: string } | null>(null)
  const dictee = useTacheDictee({
    fermer: (t) => {
      setOuvert(false)
      if (t) setCreee(t)
    },
  })

  function dicter() {
    setCreee(null)
    setOuvert(true)
    // Dans le geste : sur iPhone, le micro ne s'ouvre qu'ainsi.
    if (avecMicro) void dictee.micro.commencer()
    else dictee.saisir()
  }

  return (
    <section className="actions-rapides-bloc" aria-label="Actions rapides">
      {!ouvert && (
        <div className="actions-rapides">
          <button type="button" className="action-rapide" onClick={dicter}>
            <span className="action-rapide-icone">
              <Icone nom={avecMicro ? 'mic' : 'add_task'} />
            </span>
            <strong>{avecMicro ? 'Dicter une tâche' : 'Nouvelle tâche'}</strong>
          </button>
          <label className="action-rapide">
            <input
              type="file"
              accept="image/*"
              capture="environment"
              hidden
              onChange={(e) => {
                if (!e.target.files?.length) return
                confierPhotos(e.target.files)
                e.target.value = ''
                void navigate({ to: '/photo' })
              }}
            />
            <span className="action-rapide-icone">
              <Icone nom="photo_camera" />
            </span>
            <strong>Photographier un document</strong>
          </label>
        </div>
      )}
      {ouvert && <PanneauDictee d={dictee} />}
      {creee && !ouvert && (
        <p className="action-rapide-faite" role="status">
          <Icone nom="check_circle" />
          <span>
            Tâche créée :{' '}
            <Link to="/taches/$id" params={{ id: creee.id }}>
              {creee.titre}
            </Link>
          </span>
        </p>
      )}
      {!ouvert && (
        <p className="actions-rapides-liens discret">
          <Link to="/envois">
            Mes envois{enAttente > 0 ? ` (${enAttente} en attente d’envoi)` : ''}
          </Link>
          {' · '}
          <Link to="/photo">Galerie ou fichiers</Link>
        </p>
      )}
    </section>
  )
}

function useTacheDictee({
  fermer,
}: {
  fermer: (creee: { id: string; titre: string } | null) => void
}) {
  const client = useQueryClient()
  const [saisie, setSaisie] = useState<Saisie | null>(null)
  const [preparation, setPreparation] = useState(false)
  /** Le texte d'où viennent les champs proposés : modifié, on peut les redemander. */
  const [texteLu, setTexteLu] = useState<string | null>(null)
  const [parClaude, setParClaude] = useState(false)
  const [erreur, setErreur] = useState<string | null>(null)
  const [enCours, setEnCours] = useState(false)

  const micro = useDictee({
    pour: 'tache',
    repli: 'tapez la tâche',
    onTexte: (texte) => {
      // Première dictée : Claude met en champs ; ensuite, le texte s'ajoute aux précisions.
      if (!saisie) void preparer(texte)
      else
        setSaisie({
          ...saisie,
          texte: saisie.texte.trim() ? `${saisie.texte.trim()} ${texte}` : texte,
        })
    },
  })

  async function preparer(texte: string) {
    setPreparation(true)
    setErreur(null)
    try {
      const p = await lire(api.taches.dictee.$post({ json: { texte } }))
      setSaisie((avant) => ({
        titre: p.titre,
        texte: p.description ?? '',
        entiteId: p.entiteId ?? avant?.entiteId ?? '',
        dateDue: p.dateDue ?? '',
        urgente: p.urgente,
      }))
      setTexteLu(p.description ?? '')
      setParClaude(p.parClaude)
    } catch (e) {
      // La dictée n'est jamais perdue : le texte reste à relire, les champs à remplir.
      setSaisie((avant) => ({ ...(avant ?? SAISIE_VIDE), texte }))
      setTexteLu(null)
      setErreur(e instanceof ErreurApi ? e.titre : 'Champs non proposés : remplissez-les.')
    } finally {
      setPreparation(false)
    }
  }

  async function creer(entiteParDefaut: string | null) {
    if (!saisie) return
    setErreur(null)
    setEnCours(true)
    try {
      const t = await lire(
        api.taches.$post({
          json: corps(tacheCreation, {
            titre: saisie.titre,
            description: saisie.texte.trim() || null,
            entiteId: saisie.entiteId || entiteParDefaut,
            dateDue: saisie.dateDue || null,
            urgente: saisie.urgente,
          }),
        }),
      )
      await client.invalidateQueries()
      reinitialiser()
      fermer({ id: t.id, titre: t.titre })
    } catch (e) {
      setErreur(e instanceof ErreurApi ? e.message : 'Erreur inattendue.')
    } finally {
      setEnCours(false)
    }
  }

  function reinitialiser() {
    setSaisie(null)
    setTexteLu(null)
    setParClaude(false)
    setErreur(null)
  }

  function annuler() {
    micro.annuler()
    reinitialiser()
    fermer(null)
  }

  return {
    micro,
    saisie,
    setSaisie,
    preparation,
    texteLu,
    parClaude,
    erreur,
    enCours,
    preparer,
    creer,
    annuler,
    signaler: setErreur,
    /** Sans micro : le formulaire, vide, à taper. */
    saisir: () => setSaisie(SAISIE_VIDE),
  }
}

function Chrono() {
  const [secondes, setSecondes] = useState(0)
  useEffect(() => {
    const debut = Date.now()
    const id = setInterval(() => setSecondes(Math.floor((Date.now() - debut) / 1000)), 500)
    return () => clearInterval(id)
  }, [])
  const reste = Math.max(0, DUREE_MAX_MS / 1000 - secondes)
  return (
    <span className="mono discret">
      0:{String(secondes).padStart(2, '0')} · encore {reste} s
    </span>
  )
}

function PanneauDictee({ d }: { d: ReturnType<typeof useTacheDictee> }) {
  const prefixe = useId()
  const moi = useQuery(requeteMoi).data
  const entites = useQuery(requetes.entites).data ?? []
  const ecrivables = entites.filter((e) => !e.archivee && moi && ecrit(moi, e.id))
  // Une seule entité possible (associé) : elle va de soi.
  const seule = !moi?.administrateur && ecrivables.length === 1 ? ecrivables[0]!.id : null
  const { micro, saisie } = d

  if (!saisie) {
    return (
      <div className="carte panneau-dictee" aria-live="polite">
        {micro.etat === 'ecoute' ? (
          <>
            <button
              type="button"
              className="micro-ecoute"
              onClick={micro.arreter}
              aria-label="Terminer la dictée"
            >
              <Icone nom="stop_circle" plein />
            </button>
            <strong>Je vous écoute…</strong>
            <span className="discret">Dites la tâche, puis touchez pour terminer.</span>
            <Chrono />
          </>
        ) : micro.etat === 'transcription' || d.preparation ? (
          <>
            <span className="micro-attente">
              <Icone nom={d.preparation ? 'auto_awesome' : 'progress_activity'} />
            </span>
            <strong>{d.preparation ? 'Claude prépare la tâche…' : 'Transcription…'}</strong>
          </>
        ) : (
          <>
            {micro.message && <p className="alerte">{micro.message}</p>}
            <div className="boutons">
              <button type="button" className="bouton" onClick={() => void micro.commencer()}>
                <Icone nom="mic" />
                Réessayer
              </button>
              <button type="button" className="bouton-discret" onClick={d.saisir}>
                Taper la tâche
              </button>
            </div>
          </>
        )}
        <button type="button" className="bouton-discret" onClick={d.annuler}>
          Annuler
        </button>
      </div>
    )
  }

  const maj = (champ: Partial<Saisie>) => d.setSaisie({ ...saisie, ...champ })
  const texteModifie = d.texteLu !== null && saisie.texte.trim() !== d.texteLu.trim()
  const occupe = micro.etat !== 'repos' || d.preparation
  return (
    <form
      className="carte panneau-dictee formulaire-dictee"
      onSubmit={(e) => {
        e.preventDefault()
        if (!moi?.administrateur && !seule && !saisie.entiteId) d.signaler('Choisissez l’entité.')
        else void d.creer(seule)
      }}
      noValidate
    >
      <div className="section-titre">
        <h2>Nouvelle tâche</h2>
        {d.parClaude && (
          <span className="badge ton-ok">
            <Icone nom="auto_awesome" />
            Proposée par Claude, à relire
          </span>
        )}
      </div>
      <div className="champ">
        <label htmlFor={`${prefixe}-titre`}>
          Titre<span aria-hidden="true"> *</span>
        </label>
        <input
          id={`${prefixe}-titre`}
          value={saisie.titre}
          maxLength={300}
          onChange={(e) => maj({ titre: e.target.value })}
        />
      </div>
      <div className="champ">
        <label htmlFor={`${prefixe}-texte`}>Précisions (texte dicté)</label>
        <textarea
          id={`${prefixe}-texte`}
          rows={4}
          maxLength={5000}
          value={saisie.texte}
          onChange={(e) => maj({ texte: e.target.value })}
        />
        <div className="dictee">
          {Boolean(moi?.dictee) && dicteePossible() && (
            <button
              type="button"
              className={micro.etat === 'ecoute' ? 'bouton bouton-ecoute' : 'bouton-discret'}
              onClick={micro.etat === 'ecoute' ? micro.arreter : () => void micro.commencer()}
              disabled={micro.etat === 'transcription' || d.preparation}
              aria-pressed={micro.etat === 'ecoute'}
            >
              <Icone nom={micro.etat === 'ecoute' ? 'stop_circle' : 'mic'} />
              {micro.etat === 'ecoute'
                ? 'Arrêter'
                : micro.etat === 'transcription'
                  ? 'Transcription…'
                  : 'Compléter à la voix'}
            </button>
          )}
          {texteModifie && saisie.texte.trim() && (
            <button
              type="button"
              className="bouton-discret"
              disabled={occupe}
              onClick={() => void d.preparer(saisie.texte.trim())}
            >
              <Icone nom="auto_awesome" />
              {d.preparation ? 'Claude relit…' : 'Reproposer les champs'}
            </button>
          )}
          {micro.message && <span className="discret">{micro.message}</span>}
        </div>
      </div>
      {!seule && (
        <div className="champ">
          <label htmlFor={`${prefixe}-entite`}>
            Entité{!moi?.administrateur && <span aria-hidden="true"> *</span>}
          </label>
          <select
            id={`${prefixe}-entite`}
            value={saisie.entiteId}
            onChange={(e) => maj({ entiteId: e.target.value })}
          >
            <option value="">{moi?.administrateur ? 'Aucune' : 'Choisir…'}</option>
            {ecrivables.map((e) => (
              <option key={e.id} value={e.id}>
                {e.nom}
              </option>
            ))}
          </select>
          {moi?.administrateur && !saisie.entiteId && (
            <p className="aide">Sans entité : visible des seuls administrateurs.</p>
          )}
        </div>
      )}
      <div className="champs-ligne">
        <div className="champ">
          <label htmlFor={`${prefixe}-date`}>Pour le</label>
          <input
            id={`${prefixe}-date`}
            type="date"
            value={saisie.dateDue}
            onChange={(e) => maj({ dateDue: e.target.value })}
          />
        </div>
        <div className="champ champ-case">
          <input
            id={`${prefixe}-urgente`}
            type="checkbox"
            checked={saisie.urgente}
            onChange={(e) => maj({ urgente: e.target.checked })}
          />
          <label htmlFor={`${prefixe}-urgente`}>Urgente</label>
        </div>
      </div>
      {d.erreur && <p className="alerte">{d.erreur}</p>}
      <div className="boutons">
        <button
          type="submit"
          className="bouton bouton-grand"
          disabled={d.enCours || occupe || !saisie.titre.trim()}
        >
          {d.enCours ? 'Création…' : 'Créer la tâche'}
        </button>
        <button type="button" className="bouton-discret" onClick={d.annuler}>
          Annuler
        </button>
      </div>
    </form>
  )
}
